// Classify a work item from its facts, in code (the line-tag design, owner
// decision 2026-10-16). The model never gives a verdict.
//
// - Specification: each rule is a named predicate over the item's evidence
//   (`Ev`), composable with `and` / `or` / `not`.
// - Chain of Responsibility: `RULES` is evaluated in order and the first
//   rule that is satisfied gives the item its one main outcome.
// - Side rules (`SIDECARS`) run independently of the chain and of each
//   other: a status comment, a decision record, questions, commitments and
//   doubts can sit next to any main outcome.
//
// The outcomes reuse today's classes and reasons (`Classification`), so a
// stored row keeps its CHECK. What the old one-topic `classifyTopic` could
// not say becomes a side rule: an item can be a ticket AND post a status
// comment, and a doubt holds only the item it names.
//
// Pure, browser-safe.

import {
  byPosition,
  isOutcomeEligible,
  type Catalogue,
  type Fact,
  type FactId,
  type ItemOutcome,
  type Sidecar,
  type Subject,
  type WorkItem,
} from "./facts.js";
import type { Classification } from "./triage.js";
import type { DoubtBranch, DoubtCode, FactFunction, RefKind } from "./vocabulary.js";

// ---------------------------------------------------------------------------
// Specification
// ---------------------------------------------------------------------------

export interface Spec<T> {
  readonly name: string;
  readonly isSatisfiedBy: (t: T) => boolean;
  readonly and: (other: Spec<T>) => Spec<T>;
  readonly or: (other: Spec<T>) => Spec<T>;
  readonly not: () => Spec<T>;
}

export const spec = <T>(name: string, test: (t: T) => boolean): Spec<T> => {
  const self: Spec<T> = {
    name,
    isSatisfiedBy: test,
    and: (o) => spec(`${name}&${o.name}`, (t) => test(t) && o.isSatisfiedBy(t)),
    or: (o) => spec(`${name}|${o.name}`, (t) => test(t) || o.isSatisfiedBy(t)),
    not: () => spec(`!${name}`, (t) => !test(t)),
  };
  return self;
};

// ---------------------------------------------------------------------------
// The evidence a rule reads
// ---------------------------------------------------------------------------

/** One part of an item that can be an outcome: the root or one facet. */
export interface Unit {
  readonly subject: Subject;
  /** The unit's own primary facts, in transcript order. */
  readonly facts: readonly Fact[];
}

/**
 * An item's evidence, as the rules read it. The per-kind lists hold the
 * primary facts on the item's NEW work (units without an existing issue,
 * and the facts a facet that is an open issue gives up to it,
 * `takenOverFactsOf`) that may create an outcome (`isOutcomeEligible`);
 * the other facts on a unit that IS an open snapshot issue feed its status
 * comment instead (`existing`).
 */
export interface Ev {
  readonly aboutThisProject: Subject["aboutThisProject"];
  readonly req: readonly Fact[];
  readonly ac: readonly Fact[];
  readonly bug: readonly Fact[];
  readonly dec: readonly Fact[];
  readonly rej: readonly Fact[];
  readonly done: readonly Fact[];
  readonly com: readonly Fact[];
  readonly hedge: readonly Fact[];
  readonly q: readonly Fact[];
  readonly stat: readonly Fact[];
  /** A priority or urgency said plainly: PRIO with no HEDGE on the same
   *  fact. A priority that reaches the item only through a `prioritizes`
   *  edge is a supporting fact and is not here. */
  readonly prio: readonly Fact[];
  /** Units that carry their own request or bug (an acceptance detail never
   *  makes one); the root, when the change is a decision with a work
   *  commitment from our side and nobody asked for it. */
  readonly outcomeFacets: readonly Unit[];
  /** The units with an ask whose last word is not "done" or "rejected". */
  readonly openAsks: readonly Unit[];
  /** Facets with no ask of their own but with their own acceptance detail
   *  or work commitment, not settled: under an open root ask, parts of it. */
  readonly detailFacets: readonly Unit[];
  /** The outcomes to build: the open asks, where the root's own ask counts
   *  only while no facet has an open ask of its own. Under an open root ask
   *  a facet with its own acceptance detail or work commitment
   *  (`detailFacets`) is a part too, once the parts are two or more: the
   *  root asked for the whole and each facet says what one part must do,
   *  so the item is too broad. A root ask beside exactly one facet ask and
   *  no other part stays one outcome, and `doubtRule` asks "one or many?".
   *  Known risk: a ticket whose acceptance details the inventory split into
   *  facets becomes an epic. */
  readonly openOutcomes: readonly Unit[];
  /** Units that are an open snapshot issue, with the facts they keep (not
   *  taken over by the item's new work); only units that keep some. */
  readonly existing: readonly Unit[];
  /** The facts the per-kind lists are drawn from: on the new work, may
   *  create an outcome; in transcript order. */
  readonly eligible: readonly Fact[];
  /** Every primary fact, eligible or not. */
  readonly all: readonly Fact[];
}

const has = (f: Fact, fn: FactFunction): boolean => f.fns.includes(fn);

const CHANGE_FNS: readonly FactFunction[] = ["REQ", "BUG"];
const SETTLE_FNS: readonly FactFunction[] = ["DONE", "REJ"];

/** A commitment to work by Loom's side or an unknown speaker. */
export const isWorkCommitment = (f: Fact): boolean =>
  f.com !== null && f.com.target === "work" && f.com.by !== "customer";

/**
 * A line's word on its unit's change: its dominant kind that asks (REQ,
 * BUG) or settles (DONE, REJ); null when it says none of them. One
 * exception: a line with a request (REQ) and a DONE is settled, whichever
 * kind the tags name first (DONE answers a request, codebook `rej_done`:
 * one line that names the request and says it already exists or works
 * settles it). A bug on the line keeps tag order ("the login works now,
 * but it still crashes" is an open bug next to something done, so it is
 * asked, not settled), and so does a REJ beside a request: a line can ask
 * for one thing and turn down another.
 */
const wordOf = (f: Fact): FactFunction | null => {
  const word = f.fns.find((fn) => CHANGE_FNS.includes(fn) || SETTLE_FNS.includes(fn)) ?? null;
  return word === "REQ" && has(f, "DONE") && !has(f, "BUG") ? "DONE" : word;
};

/**
 * The fact behind a unit's last word (`lastWordOf`): its latest eligible
 * fact that asks (REQ, BUG) or settles (DONE, REJ); null when none does.
 */
export const lastWordFactOf = (u: Unit): Fact | null => {
  let last: Fact | null = null;
  for (const f of u.facts) {
    if (isOutcomeEligible(f) && wordOf(f) !== null) last = f;
  }
  return last;
};

/**
 * A unit's last word on its change: the word (`wordOf`) of its latest
 * eligible fact that asks (REQ, BUG) or settles (DONE, REJ); null when no
 * fact says one.
 */
export const lastWordOf = (u: Unit): FactFunction | null => {
  const last = lastWordFactOf(u);
  return last === null ? null : wordOf(last);
};

/** Settled when a unit's last word (`lastWordOf`) is DONE or REJ. */
export const lastWordSettled = (u: Unit): boolean => {
  const last = lastWordOf(u);
  return last !== null && SETTLE_FNS.includes(last);
};

/** The item's root, then its facets, each with its own primary facts. */
export const unitsOf = (item: Pick<WorkItem, "root" | "facets" | "primary">): Unit[] =>
  [item.root, ...item.facets].map((subject) => ({
    subject,
    facts: item.primary.filter((f) => f.subject === subject.key),
  }));

// ---------------------------------------------------------------------------
// Open issues inside an item
// ---------------------------------------------------------------------------

/** The kinds that keep a fact an open issue's own report: its dominant one
 *  says where the work stands or that it is done. */
const REPORT_FNS: readonly FactFunction[] = ["STAT", "DONE"];

/** A proposal or a decision on a unit: what makes a facet's talk work. */
const PROPOSAL_FNS: readonly FactFunction[] = ["REQ", "AC", "DEC"];

/**
 * Whether the item's new work (its units with no open issue) asks or
 * decides a change of its own that is still open: an eligible request or
 * bug on a unit whose last word is not done or rejected, or a decision with
 * a work commitment from our side.
 */
const hasOwnNewWork = (units: readonly Unit[]): boolean => {
  const fresh = units.filter((u) => u.subject.existingIid === null);
  const asks = fresh.some(
    (u) =>
      !lastWordSettled(u) &&
      u.facts.some((f) => isOutcomeEligible(f) && CHANGE_FNS.some((fn) => has(f, fn))),
  );
  const eligible = fresh.flatMap((u) => u.facts).filter(isOutcomeEligible);
  return asks || (eligible.some((f) => has(f, "DEC")) && eligible.some(isWorkCommitment));
};

/**
 * The facts a facet that IS an open issue gives up to its item's new work
 * (owner 2026-10-30, "agreed work must not land in 'not taken'"). A facet
 * qualifies when it names an open issue (never the root: a root that IS an
 * issue stays a comment on it), its last word is not done or rejected,
 * one of its eligible facts proposes or decides something (REQ, AC, DEC),
 * and the item has open new work of its own (`hasOwnNewWork`): when #N's
 * ask is all the item has, it stays a comment on #N, never a duplicate.
 * Every fact of such a facet is taken over except the ones whose dominant
 * kind is a report (STAT, DONE): those stay #N's status comment.
 */
export const takenOverFactsOf = (item: Pick<WorkItem, "root" | "facets" | "primary">): ReadonlySet<FactId> => {
  const units = unitsOf(item);
  if (!hasOwnNewWork(units)) return new Set();
  const out = new Set<FactId>();
  for (const u of units) {
    if (u.subject.key === item.root.key || u.subject.existingIid === null || lastWordSettled(u)) continue;
    if (!u.facts.some((f) => isOutcomeEligible(f) && PROPOSAL_FNS.some((fn) => has(f, fn)))) continue;
    for (const f of u.facts) if (!REPORT_FNS.some((fn) => f.fns[0] === fn)) out.add(f.id);
  }
  return out;
};

/** The facts on units that ARE an open issue and stay that issue's (not
 *  taken over): its status comment's, the item's context only. */
export const elsewhereFactsOf = (item: Pick<WorkItem, "root" | "facets" | "primary">): ReadonlySet<FactId> => {
  const taken = takenOverFactsOf(item);
  const issueKeys = new Set(
    [item.root, ...item.facets].filter((s) => s.existingIid !== null).map((s) => s.key),
  );
  return new Set(item.primary.filter((f) => issueKeys.has(f.subject) && !taken.has(f.id)).map((f) => f.id));
};

/** The open issues whose facets gave the item facts (`takenOverFactsOf`),
 *  ascending: the item's issue links them as related. */
export const takenOverIidsOf = (item: Pick<WorkItem, "root" | "facets" | "primary">): number[] => {
  const taken = takenOverFactsOf(item);
  const iids = new Set(
    item.facets.flatMap((s) =>
      s.existingIid !== null && item.primary.some((f) => f.subject === s.key && taken.has(f.id))
        ? [s.existingIid]
        : [],
    ),
  );
  return [...iids].sort((a, b) => a - b);
};

export const evidenceOf = (item: WorkItem): Ev => {
  const taken = takenOverFactsOf(item);
  const units = unitsOf(item);
  // A facet that is an open issue counts with what it gives up to the new
  // work; what it keeps is that issue's.
  const fresh = units.flatMap((u): Unit[] => {
    if (u.subject.existingIid === null) return [u];
    const facts = u.facts.filter((f) => taken.has(f.id));
    return facts.length === 0 ? [] : [{ subject: u.subject, facts }];
  });
  const existing = units.flatMap((u): Unit[] => {
    if (u.subject.existingIid === null) return [];
    const facts = u.facts.filter((f) => !taken.has(f.id));
    return facts.length === 0 ? [] : [{ subject: u.subject, facts }];
  });
  const eligible = fresh.flatMap((u) => u.facts).filter(isOutcomeEligible);
  const of = (fn: FactFunction): Fact[] => eligible.filter((f) => has(f, fn));

  const req = of("REQ");
  const ac = of("AC");
  const bug = of("BUG");
  const dec = of("DEC");
  const com = of("COM");
  const changeUnits = fresh.filter((u) =>
    u.facts.some((f) => isOutcomeEligible(f) && CHANGE_FNS.some((fn) => has(f, fn))),
  );
  // The SITEMAP rule: a decision with a work commitment from our side ("ich
  // nehme das mit") is a change even when nobody "asked" for it, with or
  // without acceptance details. A reply commitment ("ja, gut", "ich schreibe
  // dir") or a customer's is no work commitment: the decision stays one.
  const decidedChange = changeUnits.length === 0 && dec.length > 0 && com.some(isWorkCommitment);
  const rootUnit = units[0]!;
  const outcomeFacets =
    decidedChange && rootUnit.subject.existingIid === null ? [rootUnit] : changeUnits;
  const openAsks = changeUnits.filter((u) => !lastWordSettled(u));
  const openFacetAsks = openAsks.filter((u) => u.subject.key !== item.root.key);
  const rootAsks = openAsks.some((u) => u.subject.key === item.root.key);
  // A facet with no ask of its own that still says what its part must do
  // (an acceptance detail) or that someone takes it on (a work commitment).
  const detailFacets = fresh.filter(
    (u) =>
      u.subject.key !== item.root.key &&
      !changeUnits.includes(u) &&
      !lastWordSettled(u) &&
      u.facts.some((f) => isOutcomeEligible(f) && (has(f, "AC") || isWorkCommitment(f))),
  );
  // Under an open root ask the parts count once there are two or more.
  const parts = rootAsks ? fresh.filter((u) => openFacetAsks.includes(u) || detailFacets.includes(u)) : [];

  return {
    aboutThisProject: item.root.aboutThisProject,
    req,
    ac,
    bug,
    dec,
    rej: of("REJ"),
    done: of("DONE"),
    com,
    hedge: of("HEDGE"),
    q: of("Q"),
    stat: of("STAT"),
    prio: of("PRIO").filter((f) => !has(f, "HEDGE")),
    outcomeFacets,
    openAsks,
    detailFacets,
    // A decided change has no ask a later "done" could settle: it is open.
    openOutcomes: decidedChange
      ? outcomeFacets
      : parts.length >= 2
        ? parts
        : openFacetAsks.length > 0
          ? openFacetAsks
          : openAsks,
    existing,
    eligible: [...eligible].sort(byPosition),
    all: item.primary,
  };
};

// ---------------------------------------------------------------------------
// The rules, first match wins
// ---------------------------------------------------------------------------

export const grounded = spec<Ev>("grounded", (e) => e.all.length > 0);
export const otherProject = spec<Ev>("other_project", (e) => e.aboutThisProject === "other_project");
/** Someone asked for a change, or decided one with a work commitment from
 *  our side. */
export const hasChange = spec<Ev>("change", (e) => e.outcomeFacets.length > 0);
/** Per facet: every outcome's last word is done or rejected; with no
 *  outcome at all, something was already done. */
export const settled = spec<Ev>("settled", (e) =>
  e.outcomeFacets.length > 0 ? e.openOutcomes.length === 0 : e.done.length > 0,
);
/** Some ask's last word is a rejection (REJ), not "done": the meeting
 *  turned it down, a bug we won't fix too. With `settled` that is a
 *  decision, never "Schon erledigt" (owner decision 2026-10-19,
 *  "Beschluss"). */
export const rejected = spec<Ev>("rejected", (e) => e.outcomeFacets.some((u) => lastWordOf(u) === "REJ"));
/** Built but not live yet: every "done" that settles the item (each
 *  outcome's last word, else every DONE) also reports where it stands
 *  (STAT beside it, codebook `not_live_yet`). With `settled` that is a
 *  status update on the work, never new work and never "Schon erledigt"
 *  (owner 2026-10-30). */
export const notLive = spec<Ev>("not_live", (e) => {
  const dones =
    e.outcomeFacets.length > 0
      ? e.outcomeFacets.flatMap((u) => {
          const f = lastWordFactOf(u);
          return f !== null && wordOf(f) === "DONE" ? [f] : [];
        })
      : e.done;
  return dones.length > 0 && dones.every((f) => has(f, "STAT"));
});
export const statusReport = spec<Ev>("status", (e) => e.stat.length > 0 || e.existing.length > 0);
export const decided = spec<Ev>("decided", (e) => e.dec.length > 0 || e.rej.length > 0);
/** Two or more open outcomes: asks, or (under an open root ask) parts with
 *  their own acceptance detail or work commitment. */
export const broad = spec<Ev>("broad", (e) => e.openOutcomes.length >= 2);
/** Exactly one open outcome, and someone on our side takes it on. */
export const committed = spec<Ev>(
  "committed",
  (e) => e.openOutcomes.length === 1 && e.com.some(isWorkCommitment),
);
/** An open question and nothing to build: it goes on the meeting's
 *  questions ("Offene Frage"), never into an issue. */
export const asksQuestion = spec<Ev>("question", (e) => e.q.length > 0);
/** Hedged. (An item with no open outcome never gets this far: `settled`
 *  and the no-change rules come first.) */
export const idea = spec<Ev>("idea", (e) => e.hedge.length > 0);
/** A priority or urgency said plainly on the item's own work (`Ev.prio`). */
export const prioritized = spec<Ev>("prioritized", (e) => e.prio.length > 0);
/** A decision said plainly (DEC, not hedged) on the item's own work. */
const plainDecision = (e: Pick<Ev, "dec">): boolean => e.dec.some((f) => !has(f, "HEDGE"));
/** Somebody on our side agreed to the work: a work commitment from our
 *  side, or a decision said plainly (owner 2026-10-30: an idea is hedged
 *  work nobody agreed to). */
export const agreed = spec<Ev>("agreed", (e) => e.com.some(isWorkCommitment) || plainDecision(e));

export interface Rule {
  readonly spec: Spec<Ev>;
  readonly outcome: Classification;
  /** The kinds of information that decide this outcome. */
  readonly decides: readonly FactFunction[];
}

const always = spec<Ev>("otherwise", () => true);
const noChange = hasChange.not();

/**
 * The chain. Breadth and speculation are the only paths to an epic; an
 * unhedged concrete request is a ticket (owner decisions).
 *
 * An unclear project is no link of it: Loom never decides "Projekt unklar"
 * silently. The `which_project` doubt (`doubtRule`) holds the item, and the
 * rest of the chain gives what it would be once the owner says it belongs
 * here (its `held_class`: the doubt card reads "Ticket", not "Projekt
 * unklar"). The `unclear_project` reason stays for rows written before.
 *
 * Speculation stays a path to an epic, but an idea floated with nobody on
 * our side taking it on is asked, not filed (owner decision 2026-10-19,
 * "Ask me"): the `work_or_idea` doubt holds it, and the chain's outcome is
 * what it would be (`floatedIdea`). A priority or urgency said plainly on
 * the item's own work (`prioritized`: PRIO, not hedged) is no idea: the
 * item is a ticket or an epic by the rest of the chain, and nobody asks
 * "work or idea?" (owner decision 2026-10-29); so is hedged work somebody
 * on our side agreed to (`agreed`: a work commitment or a plain decision,
 * owner 2026-10-30). An ask whose last word is a rejection is a decision,
 * not "already settled" (owner decision 2026-10-19); a fix built but not
 * live yet (`notLive`) is a status update (owner 2026-10-30).
 */
export const RULES: readonly Rule[] = [
  { spec: grounded.not(), outcome: { class: "none", reason: "ungrounded" }, decides: [] },
  { spec: otherProject, outcome: { class: "none", reason: "other_project" }, decides: [] },
  // Settled by a rejection: turned down is a decision, not done.
  { spec: settled.and(rejected), outcome: { class: "none", reason: "decision_no_work" }, decides: ["DEC", "REJ"] },
  // Built but not live yet: where the work stands, not new work.
  { spec: settled.and(notLive), outcome: { class: "none", reason: "status_update" }, decides: ["DONE", "STAT"] },
  { spec: settled, outcome: { class: "none", reason: "already_settled" }, decides: ["DONE", "REJ"] },
  { spec: noChange.and(statusReport), outcome: { class: "none", reason: "status_update" }, decides: ["STAT"] },
  { spec: noChange.and(decided), outcome: { class: "none", reason: "decision_no_work" }, decides: ["DEC", "REJ"] },
  { spec: noChange.and(asksQuestion), outcome: { class: "none", reason: "question" }, decides: ["Q"] },
  { spec: noChange, outcome: { class: "none", reason: "chatter" }, decides: [] },
  { spec: broad, outcome: { class: "epic", reason: "too_broad" }, decides: ["REQ", "BUG"] },
  { spec: committed, outcome: { class: "ticket", reason: "committed" }, decides: ["REQ", "BUG", "COM"] },
  {
    spec: idea.and(prioritized.not()).and(agreed.not()),
    outcome: { class: "epic", reason: "idea" },
    decides: ["REQ", "HEDGE"],
  },
  { spec: always, outcome: { class: "ticket", reason: "requested" }, decides: ["REQ", "BUG"] },
];

// ---------------------------------------------------------------------------
// Side rules, each on its own
// ---------------------------------------------------------------------------

/** A side rule reads the item's evidence, the item, its main outcome and
 *  the kinds of information that decided it (the rule's `decides`). */
export type SidecarRule = (
  e: Ev,
  item: WorkItem,
  primary: Classification,
  decides: readonly FactFunction[],
) => Sidecar[];

const ids = (fs: readonly Fact[]): FactId[] => [...new Set(fs.map((f) => f.id))];

/** References no transcript resolves. */
export const FAR_REFS: readonly RefKind[] = ["earlier_meeting", "external_doc"];

/** Does this fact point outside the transcript? */
export const isFarRef = (f: Pick<Fact, "ref">): boolean => f.ref !== null && FAR_REFS.includes(f.ref);

/** Kinds of information that ask something of the team: a far reference
 *  is doubtful only on one of these (a report of what an e-mail said is
 *  not). */
export const FAR_REF_FUNCTIONS: readonly FactFunction[] = ["REQ", "AC", "COM", "Q"];

/** A far reference that asks something: an ask, an acceptance detail, a
 *  commitment or a question that points outside the transcript. */
export const isFarAsk = (f: Pick<Fact, "ref" | "fns">): boolean =>
  isFarRef(f) && f.fns.some((fn) => FAR_REF_FUNCTIONS.includes(fn));

/**
 * An ask passed on from a document: a request the line reads out from an
 * e-mail or a list (REQ with ref `external_doc`, codebook `req_vs_ac`). The
 * line states the ask, so the transcript holds it, and a passed-on ask is
 * enough for a ticket, a thin one too (owner decision 2026-10-18). It never
 * makes an outcome rest only on what the transcript does not hold.
 */
export const isPassedOnAsk = (f: Pick<Fact, "ref" | "fns">): boolean =>
  f.ref === "external_doc" && f.fns.includes("REQ");

/** A deciding fact the transcript does not hold: a far reference that is
 *  not an ask passed on from a document. */
const restsOutside = (f: Fact): boolean => isFarRef(f) && !isPassedOnAsk(f);

/**
 * The facts an outcome rests on: the item's facts on its new work that may
 * create an outcome (`isOutcomeEligible`, the root and facets without an
 * open issue, as `evidenceOf` reads them) and carry a kind the deciding
 * rule names (`decides`), in transcript order. A rule that names no kind
 * (`ungrounded`, `chatter`, …) rests on none.
 */
export const decidingFactsOf = (e: Pick<Ev, "eligible">, decides: readonly FactFunction[]): Fact[] =>
  e.eligible.filter((f) => f.fns.some((fn) => decides.includes(fn)));

/** A status comment per unit that is an open snapshot issue: its status
 *  reports and any ask about it (a REQ on #N is a comment on #N, never a
 *  duplicate ticket). */
export const statusCommentRule: SidecarRule = (e) =>
  e.existing.flatMap((u) => {
    const said = u.facts.filter((f) => f.fns.some((fn) => fn !== "CTX"));
    return said.length === 0 || u.subject.existingIid === null
      ? []
      : [{ kind: "status_comment", iid: u.subject.existingIid, facts: ids(said) } as const];
  });

/** A decision record for an item that files nothing. Inside a filed item a
 *  rejection is the draft's "Nicht im Umfang". */
export const decisionRule: SidecarRule = (e, _i, primary) =>
  primary.class === "none" && (e.dec.length > 0 || e.rej.length > 0)
    ? [{ kind: "decision", facts: ids([...e.dec, ...e.rej].sort((a, b) => a.seq - b.seq)) }]
    : [];

/** Each question: the item's own when it files, the meeting's otherwise. */
export const questionRule: SidecarRule = (_e, item, primary) =>
  item.primary
    .filter((f) => has(f, "Q"))
    .map((f) => ({ kind: "question", fact: f.id, scope: primary.class === "none" ? "meeting" : "item" }) as const);

/** A commitment to reply or about the process goes on the meeting's list;
 *  a work commitment is the item's own (`committed`). */
export const commitmentRule: SidecarRule = (_e, item) =>
  item.primary
    .filter((f) => f.com !== null && f.com.target !== "work")
    .map((f) => ({ kind: "commitment", fact: f.id }) as const);

/** The first line that puts the root's change forward: its first eligible
 *  REQ, in transcript order. */
const firstRootAskOf = (e: Pick<Ev, "eligible">, item: Pick<WorkItem, "root">): Fact | undefined =>
  e.eligible.find((f) => f.subject === item.root.key && has(f, "REQ"));

/**
 * An idea floated with nobody on our side taking it on: the first line
 * that puts the root's change forward (`firstRootAskOf`) is hedged (HEDGE)
 * or asked as a question (Q), no fact on the item's new work is a work
 * commitment from our side (`isWorkCommitment`), nobody said a priority
 * plainly (`Ev.prio`: a plain "das brauchen wir zuerst" makes it work,
 * owner decision 2026-10-29) and nobody decided it plainly (a DEC with no
 * HEDGE, owner 2026-10-30). A bug is no idea; a root that is an open issue
 * has no eligible facts, so it never counts.
 */
export const floatedIdea = (
  e: Pick<Ev, "eligible" | "com" | "prio" | "dec">,
  item: Pick<WorkItem, "root">,
): boolean => {
  const firstAsk = firstRootAskOf(e, item);
  return (
    firstAsk !== undefined &&
    (has(firstAsk, "HEDGE") || has(firstAsk, "Q")) &&
    settlesWorkOrIdea(e) === null
  );
};

/**
 * What answers "work now, or only an idea?" on the item's own work, the
 * strongest first: a work commitment from our side (`committed`), a plain
 * decision (`agreed`), a priority said plainly (`prioritized`); null when
 * none does. A carried `work_or_idea` doubt whose subject now says one of
 * them is retired as settled by it (`floatedIdea` raises none then).
 */
export const settlesWorkOrIdea = (
  e: Pick<Ev, "com" | "dec" | "prio">,
): "committed" | "agreed" | "prioritized" | null =>
  e.com.some(isWorkCommitment)
    ? "committed"
    : plainDecision(e)
      ? "agreed"
      : e.prio.length > 0
        ? "prioritized"
        : null;

/**
 * Whether the meeting left open if this item is done at all: its outcome is
 * an idea, a `work_or_idea` doubt holds it, or one carried over from an
 * earlier revision still asks it (`carriedCodes`). Its draft then titles it
 * as a question or neutrally (`meeting_draft`'s `open_subject`).
 */
export const isOpenSubject = (
  outcome: Pick<ItemOutcome, "primary" | "sidecars">,
  carriedCodes: readonly DoubtCode[] = [],
): boolean =>
  outcome.primary.reason === "idea" ||
  outcome.sidecars.some((s) => s.kind === "doubt" && s.code === "work_or_idea") ||
  carriedCodes.includes("work_or_idea");

/**
 * Doubts, raised instead of a silent guess:
 * - `one_or_many`: something on the item is done or rejected while a bug of
 *   it is still open (two subjects under one key?); or the root and exactly
 *   one facet each carry an open ask of their own and nothing else counts
 *   as a part (one ticket or two?). Once a second part counts
 *   (`openOutcomes` two or more) the item is too broad: no doubt;
 * - `which_project`: the inventory could not tell the project (`unclear`)
 *   of an item that carries any fact, context only too: unclear means ask,
 *   never a silent "Projekt unklar" (the chain has no such link, so the
 *   main outcome is what the item would be in this project);
 * - `unresolved_ref`: the outcome rests only on what the transcript does
 *   not hold: every fact of the kinds that decided it (`decides`,
 *   `decidingFactsOf`) points to an earlier meeting or an outside document,
 *   and the item asks something of such a source (`isFarAsk`: an ask, an
 *   acceptance detail, a commitment or a question that points out). A far
 *   reference beside facts the meeting states itself (the e-mail on the
 *   screen, read out and talked through) is context, not a doubt; so is a
 *   bare report of what the e-mail said, and so is an ask the line reads
 *   out from a document (`isPassedOnAsk`: the meeting states it); an
 *   outcome that rests on no kind (`chatter`) raises none;
 * - `work_or_idea`: an item that would file (a ticket or an epic) whose
 *   change first came up hedged and that nobody on our side takes on
 *   (`floatedIdea`): work now, or only an idea? The item is filed as what
 *   it would be and the card asks (owner decision 2026-10-29).
 * Each doubt names the branch that raised it (`branch`, `DOUBT_BRANCHES`),
 * for the classify step's counts; a settled item beside an open bug is
 * checked first when both `one_or_many` branches hold.
 */
export const doubtRule: SidecarRule = (e, item, primary, decides) => {
  const out: Sidecar[] = [];
  const subjects = [item.itemKey];
  const units = unitsOf(item);
  const settledSomewhere = item.primary.some((f) => has(f, "DONE") || has(f, "REJ"));
  const openBug = units.some((u) => u.facts.some((f) => has(f, "BUG")) && !lastWordSettled(u));
  const rootAsks = e.openAsks.some((u) => u.subject.key === item.root.key);
  const rootBesideOneFacet = rootAsks && e.openAsks.length === 2 && e.openOutcomes.length < 2;
  if ((settledSomewhere && openBug) || rootBesideOneFacet) {
    const branch: DoubtBranch = settledSomewhere && openBug ? "settled_beside_open_bug" : "root_beside_one_facet";
    out.push({ kind: "doubt", code: "one_or_many", subjects, branch });
  }
  if (e.aboutThisProject === "unclear" && item.primary.length > 0) {
    out.push({ kind: "doubt", code: "which_project", subjects, branch: "project_unclear" });
  }
  const deciding = decidingFactsOf(e, decides);
  if (deciding.length > 0 && deciding.every(restsOutside) && item.primary.some(isFarAsk)) {
    out.push({ kind: "doubt", code: "unresolved_ref", subjects, branch: "rests_outside" });
  }
  if (primary.class !== "none" && floatedIdea(e, item)) {
    const hedged = has(firstRootAskOf(e, item)!, "HEDGE");
    out.push({ kind: "doubt", code: "work_or_idea", subjects, branch: hedged ? "floated_hedged" : "floated_question" });
  }
  return out;
};

export const SIDECARS: readonly SidecarRule[] = [
  statusCommentRule,
  decisionRule,
  questionRule,
  commitmentRule,
  doubtRule,
];

/** The first rule of the chain the item satisfies, and every side rule. */
export const classifyItem = (item: WorkItem, rules: readonly Rule[] = RULES): ItemOutcome => {
  const e = evidenceOf(item);
  const rule = rules.find((r) => r.spec.isSatisfiedBy(e)) ?? RULES[RULES.length - 1]!;
  return {
    primary: rule.outcome,
    rule: rule.spec.name,
    decides: rule.decides,
    sidecars: SIDECARS.flatMap((s) => s(e, item, rule.outcome, rule.decides)),
  };
};

// ---------------------------------------------------------------------------
// Meeting-level output
// ---------------------------------------------------------------------------

/** Two items whose own lines are at least this share of the smaller one's
 *  are asked about ("one item or two?"). */
export const SAME_OR_TWO_SHARE = 0.5;

/**
 * `same_or_two` doubts between items: a line counts as shared by two items
 * when both have a primary fact on it and not both facts carry their own
 * part of the line (two parts of one sentence are two things said). Reads
 * only each item's key and its primary facts' lines and parts, so the doubt
 * card can ask it again of the stored line tags.
 */
export const sameOrTwoDoubts = (
  items: readonly (Pick<WorkItem, "itemKey"> & { readonly primary: readonly Pick<Fact, "seq" | "span">[] })[],
): Sidecar[] => {
  const out: Sidecar[] = [];
  const bySeq = items.map((i) => {
    const m = new Map<number, Pick<Fact, "seq" | "span">[]>();
    for (const f of i.primary) m.set(f.seq, [...(m.get(f.seq) ?? []), f]);
    return m;
  });
  for (let a = 0; a < items.length; a += 1) {
    for (let b = a + 1; b < items.length; b += 1) {
      const la = bySeq[a]!;
      const lb = bySeq[b]!;
      const smaller = Math.min(la.size, lb.size);
      if (smaller === 0) continue;
      let shared = 0;
      for (const [seq, fa] of la) {
        const fb = lb.get(seq);
        if (fb === undefined) continue;
        const ownParts = fa.every((f) => f.span !== null) && fb.every((f) => f.span !== null);
        if (!ownParts) shared += 1;
      }
      if (shared > 0 && shared >= SAME_OR_TWO_SHARE * smaller) {
        out.push({
          kind: "doubt",
          code: "same_or_two",
          subjects: [items[a]!.itemKey, items[b]!.itemKey],
          branch: "shared_lines",
        });
      }
    }
  }
  return out;
};

/** The subject kinds that never file: their rows come from `meetingSubjectOutcome`. */
const MEETING_SUBJECT_KINDS: ReadonlySet<string> = new Set(["source", "process"]);

/**
 * The row a `source` or `process` subject becomes (it never files): a
 * decision it carries makes it a decision record; a commitment, or any
 * process subject, a `process` row (the meeting's commitments); an open
 * question a `question` row. A source that says none of that is no row
 * (null). Facts on its facets count for it. A `work` subject is never
 * decided here: `classifyItem` decides it.
 */
export const meetingSubjectOutcome = (
  root: Pick<Subject, "key" | "kind">,
  facts: readonly Fact[],
): { readonly primary: Classification; readonly decides: readonly FactFunction[] } | null => {
  if (!MEETING_SUBJECT_KINDS.has(root.kind)) return null;
  if (facts.some((f) => has(f, "DEC") || has(f, "REJ"))) {
    return { primary: { class: "none", reason: "decision_no_work" }, decides: ["DEC", "REJ"] };
  }
  if (root.kind === "process" || facts.some((f) => f.com !== null)) {
    return { primary: { class: "none", reason: "process" }, decides: ["COM", "Q"] };
  }
  if (facts.some((f) => has(f, "Q"))) {
    return { primary: { class: "none", reason: "question" }, decides: ["Q"] };
  }
  return null;
};

/**
 * What `source` and `process` subjects give the whole meeting (they never
 * file): their commitments, their questions and, per subject, their
 * decisions.
 */
export const meetingSidecars = (catalogue: Pick<Catalogue, "subjects">, facts: readonly Fact[]): Sidecar[] => {
  const rootOf = new Map(catalogue.subjects.map((s) => [s.key, s.parentKey ?? s.key]));
  const kindOf = new Map(catalogue.subjects.map((s) => [s.key, s.kind]));
  const out: Sidecar[] = [];
  const decisions = new Map<string, FactId[]>();
  for (const f of facts) {
    const root = rootOf.get(f.subject);
    const kind = root === undefined ? undefined : kindOf.get(root);
    if (kind !== "source" && kind !== "process") continue;
    if (f.com !== null) out.push({ kind: "commitment", fact: f.id });
    if (has(f, "Q")) out.push({ kind: "question", fact: f.id, scope: "meeting" });
    if (has(f, "DEC") || has(f, "REJ")) decisions.set(root!, [...(decisions.get(root!) ?? []), f.id]);
  }
  for (const list of decisions.values()) out.push({ kind: "decision", facts: list });
  return out;
};
